import React, { useCallback, useEffect, useRef, useState } from "react";
import { MicrophoneIcon } from "@heroicons/react/24/outline";
import { StopIcon } from "@heroicons/react/24/solid";

/**
 * Mic button that appends Web Speech API transcripts into a controlled text value.
 * Renders nothing when SpeechRecognition is unavailable.
 */
const VoiceInputButton = ({ value, onChange, disabled = false, className = "" }) => {
  const valueRef = useRef(value);
  valueRef.current = value;

  const { supported, listening, toggle, stop } = useSpeechToText({
    onResult: (text) => {
      const base = (valueRef.current || "").trim();
      onChange(base ? `${base} ${text}` : text);
    },
  });

  useEffect(() => {
    if (disabled && listening) stop();
  }, [disabled, listening, stop]);

  if (!supported) return null;

  const Icon = listening ? StopIcon : MicrophoneIcon;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      title={listening ? "Stop listening" : "Voice input"}
      aria-label={listening ? "Stop listening" : "Voice input"}
      aria-pressed={listening}
      className={
        className
        || "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-theme-text opacity-60 transition hover:opacity-100 disabled:opacity-30"
      }
    >
      <Icon
        className={`h-4 w-4 ${listening ? "animate-pulse text-theme-primary opacity-100" : ""}`}
      />
    </button>
  );
};

export default VoiceInputButton;

function useSpeechToText({ onResult }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const onResultRef = useRef(onResult);
  const wantListenRef = useRef(false);
  onResultRef.current = onResult;

  const supported = typeof window !== "undefined"
    && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  const stop = useCallback(() => {
    wantListenRef.current = false;
    setListening(false);
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore
    }
    recognitionRef.current = null;
  }, []);

  const start = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    // Tear down any prior session without flipping wantListen (stop() would race onend).
    if (recognitionRef.current) {
      const prev = recognitionRef.current;
      prev.onend = null;
      prev.onerror = null;
      try {
        prev.stop();
      } catch {
        // ignore
      }
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = navigator.language || "en-US";

    recognition.onresult = (event) => {
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (event.results[i].isFinal) {
          finalText += event.results[i][0].transcript;
        }
      }
      finalText = finalText.trim();
      if (finalText) onResultRef.current(finalText);
    };

    recognition.onerror = () => {
      wantListenRef.current = false;
      setListening(false);
      recognitionRef.current = null;
    };

    // Chrome often ends on pause; restart while the user still wants voice input.
    recognition.onend = () => {
      if (!wantListenRef.current) {
        setListening(false);
        recognitionRef.current = null;
        return;
      }
      try {
        recognition.start();
      } catch {
        wantListenRef.current = false;
        setListening(false);
        recognitionRef.current = null;
      }
    };

    recognitionRef.current = recognition;
    wantListenRef.current = true;
    setListening(true);
    try {
      recognition.start();
    } catch {
      wantListenRef.current = false;
      setListening(false);
      recognitionRef.current = null;
    }
  }, []);

  const toggle = useCallback(() => {
    if (wantListenRef.current) stop();
    else start();
  }, [start, stop]);

  useEffect(() => () => {
    wantListenRef.current = false;
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore
    }
  }, []);

  return { supported, listening, toggle, stop };
}
